import { Tooltip } from "@tosize/ui";
import { RadioGroup } from "radix-ui";
import { type Dispatch, type SetStateAction, useCallback, useMemo } from "react";
import { usePostDataContext } from "../../../context/PostDataContext";
import useShapes from "../../../hooks/useShapes";
import { RadioGroupItem } from "../../RadioGroupItem";

interface ShapeTab {
	setShapeTabValue: Dispatch<SetStateAction<string>>;
}

export function ShapeCollection({ setShapeTabValue }: ShapeTab) {
	const { workpiece, onFinishSwitchCheckedChange } = usePostDataContext();
	const { shapes, setCurrentShape } = useShapes();

	const groups: string[] = useMemo(() => {
		if (shapes) {
			const availableGroups = shapes
				.sort((a, b) => a.optgroup.order - b.optgroup.order)
				.map((shape) => shape.optgroup.label);

			// Create a new array with every unique group
			return [...new Set(availableGroups)];
		}

		return [];
	}, [shapes]);

	const onValueChange = useCallback(
		(shape: string) => {
			setCurrentShape({ shape, scroll: true });
			setShapeTabValue("edit");
			onFinishSwitchCheckedChange(false);
		},
		[setCurrentShape, onFinishSwitchCheckedChange, setShapeTabValue],
	);

	return (
		<RadioGroup.Root value={workpiece.shape} onValueChange={onValueChange}>
			{groups.map((label) => (
				<div key={label}>
					<div className="my-4 flex items-center justify-center whitespace-nowrap font-bold text-shades-dark text-sm">
						<span className="h-px w-full bg-shades-medium-light" />
						<span className="px-4">{label}</span>
						<span className="h-px w-full bg-shades-medium-light" />
					</div>

					<div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-4 xl:grid-cols-4">
						{shapes
							?.filter((shape) => shape.optgroup.label === label)
							.map((shape) => (
								<Tooltip content={shape.name} key={shape.code}>
									<span>
										<RadioGroupItem value={shape.code}>
											<img src={shape.images.general} alt={shape.name} height={62.5} width={125} />
										</RadioGroupItem>
									</span>
								</Tooltip>
							))}
					</div>
				</div>
			))}
		</RadioGroup.Root>
	);
}
